import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errText } from "../lib/api";
import { StaffChrome } from "../components/StaffChrome";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Eye, TrendingUp, Megaphone, Download } from "lucide-react";
import { PendingAds } from "../components/ad-manager/PendingAds";
import { AdCreateForm } from "../components/ad-manager/AdCreateForm";
import { AdList } from "../components/ad-manager/AdList";
import { RevenueSection, OccupancySection, AnalyticsSection } from "../components/ad-manager/AdInsights";
import { RatesSection, TiersSection, WeightsSection } from "../components/ad-manager/AdPricing";
import { CouponsSection } from "../components/ad-manager/CouponsSection";
import { PaymentLog } from "../components/ad-manager/PaymentLog";

export default function AdManager() {
  const navigate = useNavigate();
  const [ads, setAds] = useState(null);
  const [rates, setRates] = useState(null);
  const [weights, setWeights] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  const load = () => api.get("/admin/ads").then((r) => setAds(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => {
    load();
    api.get("/admin/ads/rates").then((r) => setRates(r.data)).catch(() => {});
    api.get("/admin/ads/weights").then((r) => setWeights(r.data)).catch(() => {});
  }, []);

  const putAd = async (ad, changes) => {
    try {
      await api.put(`/admin/ads/${ad.id}`, {
        title: ad.title, image: ad.image, link_url: ad.link_url, placement: ad.placement,
        active: ad.active, weight: ad.weight || 1, start_at: ad.start_at || "", end_at: ad.end_at || "",
        ...changes,
      });
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const markPaid = async (id) => {
    try { await api.post(`/admin/ads/${id}/mark-paid`); toast.success("e-Transfer confirmed — ad is live"); load(); refresh(); }
    catch (e) { toast.error(errText(e)); }
  };
  const markBundlePaid = async (bundleId) => {
    try { const { data } = await api.post(`/admin/ads/bundle/${bundleId}/mark-paid`); toast.success(`Bundle confirmed — ${data.updated} ad${data.updated === 1 ? "" : "s"} live`); load(); refresh(); }
    catch (e) { toast.error(errText(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/ads/${id}/reject`); toast.success("Ad rejected"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/ads/${id}`); toast.success("Ad deleted"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/ads/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "critterposts-ads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errText(e)); }
  };

  const pending = (ads || []).filter((a) => a.status === "pending");
  const totals = (ads || []).reduce((a, ad) => ({
    live: a.live + (ad.status === "live" ? 1 : 0),
    imp: a.imp + (ad.impressions || 0),
    clk: a.clk + (ad.clicks || 0),
  }), { live: 0, imp: 0, clk: 0 });
  const totalCtr = totals.imp > 0 ? ((totals.clk / totals.imp) * 100).toFixed(1) : null;

  return (
    <StaffChrome title="Ad Manager">
      <div className="mb-6 flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="ads-back-btn" className="gap-1.5 text-slate-600"><ArrowLeft size={16} /> Back to dashboard</Button>
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="ads-export-btn" className="gap-1.5 rounded-xl"><Download size={15} /> Export CSV</Button>
      </div>

      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl text-[#12234A] flex items-center gap-2"><Megaphone size={26} className="text-[#F5551E]" /> Banner Ads</h1>
        <p className="text-sm text-slate-500 mt-1">Create ads, review advertiser submissions, schedule campaigns and weight rotation across Home, Browse and Listing pages.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl bg-white border border-slate-200/80 p-4" data-testid="ads-stat-total"><p className="text-[11px] text-slate-400 font-medium">Total ads</p><p className="text-2xl font-display font-bold text-[#12234A]">{ads ? ads.length : "—"}</p></div>
        <div className="rounded-xl bg-white border border-slate-200/80 p-4" data-testid="ads-stat-live"><p className="text-[11px] text-slate-400 font-medium">Live</p><p className="text-2xl font-display font-bold text-emerald-600">{ads ? totals.live : "—"}</p></div>
        <div className="rounded-xl bg-white border border-slate-200/80 p-4" data-testid="ads-stat-impressions"><p className="text-[11px] text-slate-400 font-medium flex items-center gap-1"><Eye size={12} /> Impressions</p><p className="text-2xl font-display font-bold text-[#12234A]">{ads ? totals.imp.toLocaleString() : "—"}</p></div>
        <div className="rounded-xl bg-[#FFF4F0] border border-[#F5551E]/20 p-4" data-testid="ads-stat-ctr"><p className="text-[11px] text-[#F5551E] font-medium flex items-center gap-1"><TrendingUp size={12} /> Avg CTR</p><p className="text-2xl font-display font-bold text-[#F5551E]">{totalCtr != null ? `${totalCtr}%` : "—"}</p></div>
      </div>

      {pending.length > 0 && <PendingAds pending={pending} onMarkPaid={markPaid} onMarkBundlePaid={markBundlePaid} onReject={reject} />}

      <div className="grid lg:grid-cols-2 gap-5">
        <AdCreateForm weights={weights} onCreated={load} />
        <AdList ads={ads} onPut={putAd} onRemove={remove} />
      </div>

      <RevenueSection refreshKey={refreshKey} />
      <OccupancySection refreshKey={refreshKey} />
      <RatesSection rates={rates} setRates={setRates} />
      <TiersSection />
      <WeightsSection weights={weights} setWeights={setWeights} />
      <AnalyticsSection rates={rates} />
      <CouponsSection />
      <PaymentLog refreshKey={refreshKey} />
    </StaffChrome>
  );
}
